import { Link } from "@tanstack/react-router";
import { Loader2, ShoppingBag, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCartStore } from "@/stores/cartStore";
import {
  formatPrice,
  calcDiscountPercent,
  type ShopifyProduct,
} from "@/lib/shopify";

const WHATSAPP_NUMBER = "923707851120";

export function ProductCard({ product }: { product: ShopifyProduct }) {
  const addItem = useCartStore((s) => s.addItem);
  const isLoading = useCartStore((s) => s.isLoading);
  
  const variant = product.node.variants.edges[0]?.node;
  const image = product.node.images.edges[0]?.node;
  const price = product.node.priceRange.minVariantPrice;
  const compareAt = product.node.compareAtPrice;
  const discount = calcDiscountPercent(price.amount, compareAt?.amount);

  const handleAdd = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!variant) return;
    await addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });
  };

  const handleBuyNow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const message = `Hello Trendora Marts, I'd like to order:\n\n${product.node.title} × 1 — ${formatPrice(price.amount)}\n\nName: \nPhone: \nDelivery Address: \nPayment: Cash on Delivery`;
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
      "_blank",
    );
  };

  return (
    <Link
      to="/product/$handle"
      params={{ handle: product.node.handle }}
      className="group block bg-card rounded-lg overflow-hidden border border-border hover-lift relative"
    >
      <div className="relative aspect-square bg-secondary overflow-hidden">
        {image ? (
          <img
            src={image.url}
            alt={image.altText || product.node.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-muted-foreground">
            <ShoppingBag className="h-10 w-10" />
          </div>
        )}
        {discount > 0 && (
          <span className="absolute top-3 left-3 bg-[#e11d48] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
            -{discount}%
          </span>
        )}
        <span className="absolute top-3 right-3 bg-[--teal] text-white text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded">
          In Stock
        </span>
      </div>
      <div className="p-4 space-y-2">
        <h3 className="font-medium line-clamp-1 group-hover:text-[--teal] transition-colors">
          {product.node.title}
        </h3>
        <div className="flex items-baseline gap-2 flex-wrap">
          <p className="font-serif text-lg font-semibold text-[--navy]">
            {formatPrice(price.amount)}
          </p>
          {compareAt && (
            <p className="text-sm text-muted-foreground line-through">
              {formatPrice(compareAt.amount)}
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button
            onClick={handleAdd}
            variant="dark"
            size="sm"
            className="w-full"
            disabled={isLoading || !variant}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to Cart"}
          </Button>
          <Button
            onClick={handleBuyNow}
            size="sm"
            className="w-full bg-[#25D366] hover:bg-[#1ebe5a] text-white"
          >
            <MessageCircle className="h-3.5 w-3.5 mr-1" /> Buy Now
          </Button>
        </div>
      </div>
    </Link>
  );
}
